<template>
  <div
    tabindex="0"
    :class="{ 'product-card': true, selected: isSelected }"
    role="button"
    @click="onSelect"
    @keydown.enter.space="onSelect"
  >
    <Row :has-line="false">
      <template #top-left>
        <ChipStatus
          class="product-card__chip"
          :status="chipStatus"
          :label="product.risk.formatted"
        />
      </template>
      <template #bottom-left>
        <BaseText size="p3" :color="NebraskaColors.elementPrimary">
          {{ product.name }}
        </BaseText>
      </template>
    </Row>
    <hr class="product-card__divider" />
    <Grid :columns="[2, 3, 3, 2]">
      <Row v-for="(item, index) in product.info" :key="index" :has-line="false">
        <template #top-left>
          <BaseText size="p5">{{ item.label }}</BaseText>
        </template>
        <template #bottom-left>
          <BaseText size="p5" :color="NebraskaColors.elementPrimary">
            {{ item.value }}
          </BaseText>
        </template>
      </Row>
    </Grid>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Grid, Row, ChipStatus, BaseText } from '@warrenbrasil/nebraska-web';

import { IProductGetLastState } from '@/modules/catalog/services/products-acquisition';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface Props {
  product: IProductGetLastState;
  addProduct?: boolean;
  isSelected: boolean;
}

interface EmitEvents {
  (event: 'handle-instrument-click', { id }: { id: string }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<EmitEvents>();

const chipStatus = computed(() => {
  const mapLevelRisk: { [key: string]: string } = {
    LOW: 'positive',
    MEDIUM: 'alert',
    HIGH: 'negative'
  };

  return mapLevelRisk[props.product.risk.value];
});

function onSelect() {
  emit('handle-instrument-click', {
    id: props.product.productId ?? props.product.id
  });
}
</script>

<style lang="less" scoped>
.product-card {
  background: @background-secondary;
  width: 100%;
  padding: @size-spacing-x400;
  cursor: pointer;
  border: solid @size-border-x400 @divider-primary;
  border-radius: @size-radius-x200;
  margin-bottom: @size-spacing-x300;

  :deep(.row-item) {
    background-color: transparent !important;
  }

  &.selected {
    background: @background-tertiary;
  }

  &__chip {
    margin-bottom: @size-spacing-x150;
  }

  &__divider {
    border-top: solid @size-border-x400 @divider-primary!important;
  }

  &:hover {
    background: @background-primary;

    &.selected {
      background: @background-tertiary;
    }
  }
}
</style>
